// src/util/motion/index.ts
import { defineComponent, h, computed } from 'vue'
import 'animate.css/animate.min.css'

/**
 * Vue动画组件，封装常用Animate.css动画效果
 * @component
 * @prop {string} type - 动画类型，支持fadeIn/slideInUp/slideInDown/zoomIn/fadeOut/slideOutUp/slideOutDown/bounce
 * @prop {number} duration - 动画持续时间（毫秒），必须大于0
 * @prop {number} delay - 动画延迟时间（毫秒），不能为负数
 * @prop {number} repeat - 动画重复次数，0表示不重复
 * @returns {VNode} 包含动画效果的div容器元素
 */
export const Motion = defineComponent({
  name: 'Motion',
  props: {
    type: {
      type: String,
      default: 'fadeIn',
      validator: (value: string) => [
        // 入场动画
        'fadeIn', 'fadeInUp', 'fadeInDown', 'fadeInLeft', 'fadeInRight',
        'slideInUp', 'slideInDown', 'slideInLeft', 'slideInRight',
        'zoomIn', 'zoomInUp', 'zoomInDown', 'zoomInLeft', 'zoomInRight',
        'rotateIn', 'rotateInUpLeft', 'rotateInUpRight',
        // 退场动画
        'fadeOut', 'fadeOutUp', 'fadeOutDown', 'fadeOutLeft', 'fadeOutRight',
        'slideOutUp', 'slideOutDown', 'slideOutLeft', 'slideOutRight',
        // 特殊效果
        'bounce', 'flash', 'pulse', 'rubberBand', 'shakeX', 'shakeY',
        'headShake', 'swing', 'tada', 'wobble', 'jello', 'heartBeat'
      ].includes(value)
    },
    duration: {
      type: Number,
      default: 600,
      validator: (v: number) => v > 0
    },
    delay: {
      type: Number,
      default: 0,
      validator: (v: number) => v >= 0
    },
    repeat: {
      type: Number,
      default: 1,
      validator: (v: number) => v >= 0
    }
  },
  setup(props, { slots }) {
    // 计算生成动画CSS类，包含基础动画类、类型类、延迟类和重复类
    const animationClass = computed(() => ({
      'animate__animated': true,
      [`animate__${props.type}`]: true,
      [`animate__delay-${props.delay}ms`]: props.delay > 0,
      [`animate__repeat-${props.repeat}`]: props.repeat > 0
    }))

    // 计算生成动画样式，包含自定义持续时间和优化渲染的样式属性
    const animationStyle = computed(() => ({
      '--animate-duration': `${props.duration}ms`,
      '--animate-delay': `${props.delay}ms`,
      animationFillMode: 'both',
      willChange: 'transform, opacity'
    }))

    // 渲染带有动画效果的div容器，插入默认插槽内容
    return () => h('div', {
      class: ['motion-container', animationClass.value],
      style: animationStyle.value
    }, slots.default?.() ?? [])
  }
})